/**
 * Admin: per-Service-Group simulation library + methodology library + AI
 * feedback visibility configuration.
 *
 * Three side-by-side sections:
 *   Scenarios catalogue → tick "In library" + "Default"
 *   Methodologies catalogue → tick "In library" + "Default"
 *   Feedback visibility → radio (auto / supervisor_gated / never)
 *
 * Per-therapist additional grants live on the Clinical Supervisor's therapist
 * Simulations tab (mirrors Assessments).
 */

import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Stack, Checkbox, FormControlLabel, Button, Alert,
  CircularProgress, Divider, Chip, Radio, RadioGroup, FormLabel, Switch
} from '@mui/material';
import api from '../../../services/api';

export default function ServiceGroupSimulationSettings({ groupId, token, onClose }) {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [groupConfig, setGroupConfig] = useState(null);
  const [catalogue,  setCatalogue]    = useState(null);

  // Mutable state
  const [library,  setLibrary]  = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [methLibrary,  setMethLibrary]  = useState([]);
  const [methDefaults, setMethDefaults] = useState([]);
  const [feedbackVisibility, setFeedbackVisibility] = useState('supervisor_gated');
  const [imageGenerationEnabled, setImageGenerationEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sgRes, catRes] = await Promise.all([
          api.get(`/simulation-governance/admin/service-groups/${groupId}/simulations`, token),
          api.get('/simulation-governance/admin/platform-catalogue', token)
        ]);
        if (cancelled) return;
        setGroupConfig(sgRes?.data || null);
        setCatalogue(catRes?.data || null);
        setLibrary(sgRes?.data?.library || []);
        setDefaults(sgRes?.data?.defaults || []);
        setMethLibrary(sgRes?.data?.methodologyLibrary || []);
        setMethDefaults(sgRes?.data?.methodologyDefaults || []);
        setFeedbackVisibility(sgRes?.data?.feedbackVisibility || 'supervisor_gated');
        setImageGenerationEnabled(!!sgRes?.data?.imageGenerationEnabled);
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Could not load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, token]);

  function toggle(set, setSet, defaultsSet, setDefaultsSet, id) {
    setSet((prev) => {
      const next = prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id];
      // If removed from library, also remove from defaults
      if (!next.includes(id)) setDefaultsSet((d) => d.filter((k) => k !== id));
      return next;
    });
  }

  function toggleDefault(libraryArr, set, setSet, id) {
    if (!libraryArr.includes(id)) return;
    setSet((prev) => prev.includes(id) ? prev.filter((k) => k !== id) : [...prev, id]);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.patch(
        `/simulation-governance/admin/service-groups/${groupId}/simulations`,
        { library, defaults, methodologyLibrary: methLibrary, methodologyDefaults: methDefaults, feedbackVisibility, imageGenerationEnabled },
        token
      );
      onClose?.({ saved: true });
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }
  if (!catalogue) {
    // Catalogue load failed (most often: platform service group missing/
    // misconfigured server-side). Surface the error so it's not invisible
    // — returning null here previously produced a silent blank modal.
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Could not load the platform catalogue. Check that the platform-authoring service group exists.'}
        </Alert>
        {onClose && <Button onClick={() => onClose()}>Close</Button>}
      </Box>
    );
  }

  const scenarios = catalogue.scenarios || [];
  const methodologies = catalogue.methodologies || [];

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        <strong>Library</strong> = which platform-authored scenarios + rubrics this clinic has licensed.{' '}
        <strong>Default</strong> = subset auto-granted to every therapist. Per-therapist add-ons live on the Clinical Supervisor's therapist page.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip size="small" color="primary" variant="outlined" label={`Scenarios: ${library.length}/${scenarios.length}`} />
        <Chip size="small" color="primary" variant="outlined" label={`Methodologies: ${methLibrary.length}/${methodologies.length}`} />
        <Chip size="small" color="success" variant="outlined" label={`Default scenarios: ${defaults.length}`} />
        <Chip size="small" color="success" variant="outlined" label={`Default methodologies: ${methDefaults.length}`} />
      </Stack>

      {/* AI feedback visibility */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}>
          AI feedback visibility (therapist-practice sessions)
        </FormLabel>
        <RadioGroup value={feedbackVisibility} onChange={(e) => setFeedbackVisibility(e.target.value)}>
          <FormControlLabel value="auto" control={<Radio size="small" />}
            label="Auto — therapist sees AI scores immediately" />
          <FormControlLabel value="supervisor_gated" control={<Radio size="small" />}
            label="Supervisor-gated (recommended) — supervisor reviews then releases" />
          <FormControlLabel value="never" control={<Radio size="small" />}
            label="Never — AI scores are QA-only; therapist never sees them" />
        </RadioGroup>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Protects therapists from harsh AI marking during early adoption. Per-therapist override available
          on the Clinical Supervisor's therapist Simulations tab.
        </Typography>
      </Box>

      {/* Persona image generation opt-in */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.5, color: 'text.primary' }}>
          Persona image generation
        </FormLabel>
        <FormControlLabel
          control={
            <Switch
              checked={imageGenerationEnabled}
              onChange={(e) => setImageGenerationEnabled(e.target.checked)}
            />
          }
          label={imageGenerationEnabled
            ? 'Enabled — lecturers in this group can generate AI portraits for their personas'
            : 'Disabled — only platform admins can generate persona images'}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Uses the platform's configured image model. Generated images are watermarked.
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Scenarios</Typography>
      {scenarios.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No platform-authored scenarios published yet. Author scenarios from the Training admin page,
          ensuring they are scoped to the platform-authoring service group.
        </Alert>
      ) : (
        <Stack divider={<Divider />} spacing={0} sx={{ mb: 3 }}>
          {scenarios.map((s) => {
            const inLib = library.includes(String(s._id));
            const inDef = defaults.includes(String(s._id));
            return (
              <Stack key={s._id} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
                justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {s.name}
                    {s.difficulty && <Chip size="small" sx={{ ml: 1 }} label={s.difficulty} variant="outlined" />}
                    {s.category && <Chip size="small" sx={{ ml: 0.5 }} label={s.category} variant="outlined" />}
                  </Typography>
                  {s.scenarioContext?.presentingComplaint && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {s.scenarioContext.presentingComplaint.slice(0, 160)}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <FormControlLabel control={
                    <Checkbox size="small" checked={inLib}
                      onChange={() => toggle(library, setLibrary, defaults, setDefaults, String(s._id))} />
                  } label="In library" />
                  <FormControlLabel control={
                    <Checkbox size="small" checked={inDef} disabled={!inLib}
                      onChange={() => toggleDefault(library, defaults, setDefaults, String(s._id))} />
                  } label="Default" />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      <Typography variant="subtitle1" sx={{ mb: 1 }}>Evaluation rubrics</Typography>
      {methodologies.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>No evaluation methodologies in the catalogue yet.</Alert>
      ) : (
        <Stack divider={<Divider />} spacing={0}>
          {methodologies.map((m) => {
            const inLib = methLibrary.includes(String(m._id));
            const inDef = methDefaults.includes(String(m._id));
            return (
              <Stack key={m._id} direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }}
                justifyContent="space-between" spacing={1} sx={{ py: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2">
                    {m.name}
                    {m.organisation && <Chip size="small" sx={{ ml: 1 }} label={m.organisation} variant="outlined" />}
                  </Typography>
                  {m.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {m.description.slice(0, 160)}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                  <FormControlLabel control={
                    <Checkbox size="small" checked={inLib}
                      onChange={() => toggle(methLibrary, setMethLibrary, methDefaults, setMethDefaults, String(m._id))} />
                  } label="In library" />
                  <FormControlLabel control={
                    <Checkbox size="small" checked={inDef} disabled={!inLib}
                      onChange={() => toggleDefault(methLibrary, methDefaults, setMethDefaults, String(m._id))} />
                  } label="Default" />
                </Stack>
              </Stack>
            );
          })}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button onClick={() => onClose?.()} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
      </Stack>
    </Box>
  );
}
